import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, 
  CircularProgress, Alert, Grid, Divider, Breadcrumbs, Link,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Checkbox, Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Print as PrintIcon, 
  ArrowBack as BackIcon,
  LocalShipping as ShippingIcon,
  Person as DriverIcon,
  CalendarToday as DateIcon,
  LocationCity as CityIcon,
  PlayArrow as DispatchIcon,
  CheckCircle as FinishIcon,
  Undo as UndoIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  RemoveCircleOutline as RemoveIcon
} from '@mui/icons-material';
import tripService from '../services/tripService';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/FeedbackContext';

const getStatusColor = (status) => {
  switch (status) {
    case 'borrador': return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
    case 'en_curso': return { bg: '#e3f2fd', text: '#1976d2', border: '#90caf9' };
    case 'finalizado': return { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' };
    case 'cancelado': return { bg: '#ffebee', text: '#d32f2f', border: '#ef9a9a' };
    default: return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
  }
};

// Estado de cada parada (TripOrder.status)
const STOP_STATUS_STYLES = {
  pendiente: { bg: '#fff8e1', text: '#8d6e00', border: '#ffe082' },
  entregado: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  no_entregado: { bg: '#ffebee', text: '#d32f2f', border: '#ef9a9a' }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'borrador': return 'Borrador';
    case 'en_curso': return 'En Curso';
    case 'finalizado': return 'Finalizado';
    case 'cancelado': return 'Cancelado';
    default: return status;
  }
};

// Acciones disponibles según el estado actual (mismas transiciones que TripService.STATUS_TRANSITIONS)
const CANCEL_ACTION = {
  status: 'cancelado',
  label: 'Cancelar viaje',
  icon: CancelIcon,
  variant: 'outlined',
  color: '#d32f2f',
  confirm: 'Los pedidos del viaje se liberan y vuelven a quedar pendientes para armar otro viaje. Esta acción no se puede deshacer.'
};

const STATUS_ACTIONS = {
  borrador: [
    {
      status: 'en_curso',
      label: 'Despachar viaje',
      icon: DispatchIcon,
      variant: 'contained',
      color: '#1976d2',
      confirm: 'El viaje pasa a En curso. Desde ahora los pedidos que se confirmen ya no se suman solos a este viaje.'
    },
    CANCEL_ACTION
  ],
  en_curso: [
    {
      status: 'finalizado',
      label: 'Finalizar viaje',
      icon: FinishIcon,
      variant: 'contained',
      color: '#2e7d32',
      confirm: 'El viaje queda cerrado en el historial de reparto y no se puede volver a abrir.'
    },
    {
      status: 'borrador',
      label: 'Volver a borrador',
      icon: UndoIcon,
      variant: 'outlined',
      color: '#616161',
      confirm: 'El viaje vuelve a Borrador y los pedidos que se confirmen para sus ciudades se vuelven a sumar solos.'
    },
    CANCEL_ACTION
  ]
};

const TripDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const confirm = useConfirm();
  const [trip, setTrip] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedPending, setSelectedPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [savingOrders, setSavingOrders] = useState(false);
  const [removingOrderId, setRemovingOrderId] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const tripData = await tripService.getTripDetail(id);
      setTrip(tripData);
      const reportData = await tripService.getTripReport(id);
      setReport(reportData);
    } catch (err) {
      setError('Error al cargar el detalle del viaje.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenAdd = async () => {
    setAddOpen(true);
    setSelectedPending([]);
    try {
      setLoadingPending(true);
      // Mismo listado que al armar el viaje: pedidos sin viaje de sus ciudades
      const data = trip.route
        ? await tripService.getPendingOrdersByRoute(trip.route)
        : await tripService.getPendingOrdersByCity(trip.city);
      setPendingOrders(data);
    } catch (err) {
      setStatusError('Error al cargar los pedidos pendientes.');
      setAddOpen(false);
    } finally {
      setLoadingPending(false);
    }
  };

  const handleAddOrders = async () => {
    try {
      setSavingOrders(true);
      setStatusError('');
      await tripService.addTripOrders(id, selectedPending);
      setAddOpen(false);
      await loadData();
    } catch (err) {
      setStatusError(err?.response?.data?.error || 'No se pudieron agregar los pedidos.');
    } finally {
      setSavingOrders(false);
    }
  };

  const handleRemoveOrder = async (orderInfo) => {
    const ok = await confirm({
      title: 'Quitar pedido del viaje',
      message: `¿Quitar el pedido #${orderInfo.order_number} del viaje? Vuelve a quedar pendiente para otro viaje.`,
      confirmText: 'Quitar',
      danger: true,
    });
    if (!ok) return;
    try {
      setRemovingOrderId(orderInfo.order_id);
      setStatusError('');
      await tripService.removeTripOrder(id, orderInfo.order_id);
      await loadData();
    } catch (err) {
      setStatusError(err?.response?.data?.error || 'No se pudo quitar el pedido.');
    } finally {
      setRemovingOrderId(null);
    }
  };

  const handleConfirmStatus = async () => {
    try {
      setChangingStatus(true);
      setStatusError('');
      await tripService.updateTripStatus(id, pendingAction.status);
      setPendingAction(null);
      // Al cancelar se liberan los pedidos: hay que recargar también la planilla
      await loadData();
    } catch (err) {
      setStatusError(err?.response?.data?.error || 'No se pudo cambiar el estado del viaje.');
      setPendingAction(null);
    } finally {
      setChangingStatus(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: '#800020' }} />
        </Box>
      </Box>
    );
  }

  if (error || !trip) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, p: 4 }}>
          <Alert severity="error">{error || 'Viaje no encontrado'}</Alert>
        </Box>
      </Box>
    );
  }

  const statusStyle = getStatusColor(trip.status);
  // Cambiar estado y editar pedidos lo valida el backend con IsAdminUser
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const statusActions = isStaff ? (STATUS_ACTIONS[trip.status] || []) : [];
  const canEditOrders = isStaff && trip.status === 'borrador';
  const showStopStatus = trip.status !== 'borrador';
  const undeliveredCount = (report?.orders || []).filter((o) => o.status !== 'entregado').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }} className="main-box">
      <Box className="no-print">
        <Sidebar onLogout={handleLogout} />
      </Box>
      
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }} className="print-container">
        {/* Breadcrumbs - No visible on print */}
        <Breadcrumbs sx={{ mb: 3 }} className="no-print">
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/trips'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Viajes
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Detalle #{trip.id}
          </Typography>
        </Breadcrumbs>

        {/* Header - No visible on print */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: 4, gap: 2 }} className="no-print">
          <IconButton onClick={() => navigate('/trips')} sx={{ bgcolor: 'white', border: '1px solid #d0dbe7', color: '#0e141b' }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h3" sx={{ color: '#0e141b', fontSize: { xs: '1.875rem', md: '2.25rem' }, fontWeight: 900, mb: 0.5, letterSpacing: '-0.033em' }}>
              Viaje #{trip.id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={getStatusLabel(trip.status)} 
                size="small"
                sx={{ 
                  bgcolor: statusStyle.bg, 
                  color: statusStyle.text, 
                  fontWeight: 700, 
                  border: `1px solid ${statusStyle.border}`,
                  fontSize: '0.75rem'
                }} 
              />
              <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                Destino: {trip.city}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {statusActions.map((action) => {
            const ActionIcon = action.icon;
            return (
              <Button
                key={action.status}
                variant={action.variant}
                startIcon={<ActionIcon />}
                onClick={() => setPendingAction(action)}
                disabled={changingStatus}
                sx={{
                  ...(action.variant === 'contained'
                    ? { bgcolor: action.color, '&:hover': { bgcolor: action.color, filter: 'brightness(0.9)' } }
                    : { color: action.color, borderColor: action.color, bgcolor: 'white' }),
                  borderRadius: '0.5rem',
                  fontWeight: 700,
                  textTransform: 'none'
                }}
              >
                {action.label}
              </Button>
            );
          })}
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ 
              bgcolor: '#0e141b', 
              '&:hover': { bgcolor: '#000' },
              borderRadius: '0.5rem',
              px: 3,
              fontWeight: 700,
              textTransform: 'none'
            }}
          >
            Imprimir Planilla
          </Button>
          </Box>
        </Box>

        {statusError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStatusError('')} className="no-print">
            {statusError}
          </Alert>
        )}

        <Dialog open={Boolean(pendingAction)} onClose={() => !changingStatus && setPendingAction(null)}>
          <DialogTitle sx={{ fontWeight: 800 }}>{pendingAction?.label}</DialogTitle>
          <DialogContent>
            <DialogContentText>{pendingAction?.confirm}</DialogContentText>
            {pendingAction?.status === 'finalizado' && undeliveredCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {undeliveredCount === 1 ? 'Queda 1 parada' : `Quedan ${undeliveredCount} paradas`} sin registrar como entregada.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPendingAction(null)} disabled={changingStatus} sx={{ textTransform: 'none', color: '#4e7397' }}>
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmStatus}
              disabled={changingStatus}
              startIcon={changingStatus ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ bgcolor: pendingAction?.color, '&:hover': { bgcolor: pendingAction?.color }, textTransform: 'none', fontWeight: 700 }}
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={addOpen} onClose={() => !savingOrders && setAddOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800 }}>Agregar pedidos al viaje</DialogTitle>
          <DialogContent dividers>
            {loadingPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#800020' }} />
              </Box>
            ) : pendingOrders.length === 0 ? (
              <Typography sx={{ color: '#4e7397', py: 2, textAlign: 'center' }}>
                No hay pedidos pendientes en las ciudades de este viaje.
              </Typography>
            ) : (
              pendingOrders.map((order) => {
                const checked = selectedPending.includes(order.id);
                return (
                  <Box
                    key={order.id}
                    onClick={() => setSelectedPending((prev) => (checked ? prev.filter((oid) => oid !== order.id) : [...prev, order.id]))}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    <Checkbox checked={checked} sx={{ color: '#d0dbe7', '&.Mui-checked': { color: '#800020' } }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        #{order.order_number} · {order.client?.name}
                      </Typography>
                      <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
                        {order.route_stop ? `Parada ${order.route_stop.order} · ${order.route_stop.city_name}` : order.client?.delivery_address}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.875rem' }}>{order.total_weight} kg</Typography>
                  </Box>
                );
              })
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddOpen(false)} disabled={savingOrders} sx={{ textTransform: 'none', color: '#4e7397' }}>
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={handleAddOrders}
              disabled={savingOrders || selectedPending.length === 0}
              startIcon={savingOrders ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
              sx={{ bgcolor: '#800020', '&:hover': { bgcolor: '#600010' }, textTransform: 'none', fontWeight: 700 }}
            >
              Agregar {selectedPending.length > 0 ? `(${selectedPending.length})` : ''}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Content Area */}
        <Paper 
          sx={{ 
            p: { xs: 3, md: 5 }, 
            borderRadius: '1rem', 
            border: '1px solid #d0dbe7', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            bgcolor: 'white'
          }} 
          className="report-paper"
        >
          {/* Internal Header for Print */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 5, borderBottom: '2px solid #800020', pb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#0e141b', mb: 1, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>PLANILLA DE REPARTO</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateIcon sx={{ color: '#4e7397', fontSize: '1rem' }} />
                  <Typography sx={{ fontWeight: 700 }}>{trip.date.split('-').reverse().join('/')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CityIcon sx={{ color: '#4e7397', fontSize: '1rem' }} />
                  <Typography sx={{ fontWeight: 700 }}>{trip.city}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, color: '#800020' }}>VIAJE #{trip.id}</Typography>
              <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Sistema de Gestión Capriata</Typography>
            </Box>
          </Box>

          {/* Info Summary */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '0.75rem', borderLeft: '4px solid #800020' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700, mb: 1 }}>Repartidor</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DriverIcon sx={{ color: '#0e141b' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{trip.driver?.first_name || 'Sin Asignar'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '0.75rem', borderLeft: '4px solid #800020' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700, mb: 1 }}>Pedidos Totales</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ShippingIcon sx={{ color: '#0e141b' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{report?.orders?.length || 0} entregas</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} className="no-print">
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '0.75rem', borderLeft: '4px solid #800020' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700, mb: 1 }}>Carga Total</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', pl: 0.5 }}>
                  {report?.orders?.reduce((acc, o) => acc + (parseFloat(o.total_weight) || 0), 0).toFixed(2)} kg
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, borderBottom: '1px solid #f0f0f0', pb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#0e141b' }}>
              Hoja de Ruta y Entregas
            </Typography>
            {canEditOrders && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                className="no-print"
                sx={{ color: '#800020', fontWeight: 700, textTransform: 'none' }}
              >
                Agregar pedidos
              </Button>
            )}
          </Box>

          {/* Orders detail */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {report?.orders?.map((orderInfo, index) => (
              <Box key={orderInfo.order_number} sx={{ pageBreakInside: 'avoid' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ width: 32, height: 32, bgcolor: '#0e141b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontWeight: 900, fontSize: '1rem' }}>
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: '#0e141b' }}>{orderInfo.client_name}</Typography>
                      <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', fontWeight: 600 }}>{orderInfo.address}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Pedido #{orderInfo.order_number}</Typography>
                      <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Tel: {orderInfo.phone}</Typography>
                      {showStopStatus && (
                        <Chip
                          label={orderInfo.status_display}
                          size="small"
                          className="no-print"
                          sx={{
                            mt: 0.5,
                            bgcolor: STOP_STATUS_STYLES[orderInfo.status]?.bg,
                            color: STOP_STATUS_STYLES[orderInfo.status]?.text,
                            border: `1px solid ${STOP_STATUS_STYLES[orderInfo.status]?.border}`,
                            fontWeight: 700,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                    {canEditOrders && (
                      <Tooltip title="Quitar del viaje">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveOrder(orderInfo)}
                            disabled={removingOrderId !== null}
                            className="no-print"
                            sx={{ color: '#d32f2f' }}
                          >
                            {removingOrderId === orderInfo.order_id ? <CircularProgress size={18} color="inherit" /> : <RemoveIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #f0f0f0', borderRadius: '0.5rem', mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4e7397' }}>PRODUCTO</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4e7397' }}>CANTIDAD</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4e7397', width: { xs: 70, md: '150px' } }}>ESTADO ENTREGA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderInfo.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 600 }}>{item.product_name}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{item.quantity}</TableCell>
                          <TableCell sx={{ borderLeft: '1px solid #f0f0f0' }}></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Box sx={{ borderBottom: '1px dotted #ccc', minHeight: '40px', display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#4e7397', fontSize: '0.7rem' }}>Observaciones:</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ borderBottom: '1px dotted #ccc', minHeight: '40px', display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#4e7397', fontSize: '0.7rem' }}>Firma Conformidad:</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>

          {report?.orders?.length === 0 && (
            <Box sx={{ py: 10, textAlign: 'center' }}>
              <ShippingIcon sx={{ fontSize: 64, color: '#d0dbe7', mb: 2 }} />
              <Typography sx={{ color: '#4e7397' }}>No hay pedidos asignados a este viaje.</Typography>
            </Box>
          )}

          {/* Special notes for print */}
          <Box className="print-only" sx={{ display: 'none', mt: 10, pt: 5, borderTop: '2px solid #f0f0f0' }}>
            <Grid container spacing={6}>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #000', mt: 8, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700 }}>Firma del Chofer</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #000', mt: 8, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700 }}>Despacho / Salida</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>

      {/* Global Print Styles */}
      <style>{`
        @media print {
          title { visibility: hidden; }
          .no-print { display: none !important; }
          .main-box { background-color: white !important; display: block !important; }
          .print-container { padding: 0 !important; margin: 0 !important; max-width: 100% !important; flex: none !important; }
          .report-paper { border: none !important; padding: 0 !important; box-shadow: none !important; }
          .MuiTableRow-root { break-inside: avoid; }
          .print-only { display: block !important; }
          @page { margin: 1.5cm; }
        }
      `}</style>
    </Box>
  );
};

export default TripDetailPage;
